import { useEffect, useState } from 'react';
import { api } from '../../api';
import type { RedesPublicas } from '../../api/types';
import { t } from '../../i18n/idioma';
import { REDES_PADRAO, redesDe } from '../../screens/landing/links';
import { RAIZ as WIKI } from '../../screens/wiki/rota';
import { tempoAteVotar, useVotoTopIdle } from './useVotoTopIdle';

/**
 * Discord, Wiki e Votar em cima do chat (docs/prototipos/chat-atalhos-nota.md; o Votar é da
 * docs/techspec/techspec-topidle-votos.md). Só existem com o chat visível: o ChatHud
 * não os monta com o chat minimizado nem na linha de fala do celular. Os dois abrem em outra aba.
 * O convite do Discord é o de Configurações › Redes no console (o mesmo da landing); sem servidor, o padrão.
 */
let redesCache: RedesPublicas | null = null;
let redesPedido: Promise<RedesPublicas> | null = null;

function carregarRedes(): Promise<RedesPublicas> {
  redesPedido ??= api.estadoAcesso()
    .then(e => (redesCache = redesDe(e.redes)))
    .catch(() => REDES_PADRAO);
  return redesPedido;
}

export function useRedes(): RedesPublicas {
  const [redes, setRedes] = useState<RedesPublicas>(redesCache ?? REDES_PADRAO);
  useEffect(() => {
    if (redesCache) return;
    let vivo = true;
    void carregarRedes().then(r => { if (vivo) setRedes(r); });
    return () => { vivo = false; };
  }, []);
  return redes;
}

export function ChatAtalhos({ folha = false }: { folha?: boolean }) {
  const redes = useRedes();
  return (
    <div
      className={folha ? 'ct-atalhos ct-atalhos--folha' : 'ct-atalhos'}
      role="group"
      aria-label={t('chat.atalho.grupo')}
      // não começa o arrasto da janela do chat
      onPointerDown={e => e.stopPropagation()}
    >
      <a className="ct-at ct-at--discord" href={redes.discord} target="_blank" rel="noopener noreferrer" title={t('chat.atalho.discordDica')}>
        {/* ícone genérico de balão; o oficial do kit de marca do Discord pode entrar aqui */}
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M5 4h14a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-6l-4.5 3.4c-.6.4-1.5 0-1.5-.8V18H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Zm3.5 6.2a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6Zm7 0a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6Z" />
        </svg>
        {t('chat.atalho.discord')}
      </a>
      <a className="ct-at ct-at--wiki" href={WIKI} target="_blank" rel="noopener noreferrer" title={t('chat.atalho.wikiDica')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 5.5C6.5 4 9.5 4 12 5.8 14.5 4 17.5 4 20 5.5V19c-2.5-1.5-5.5-1.5-8 .3-2.5-1.8-5.5-1.8-8-.3Z" />
          <path d="M12 5.8v13.5" />
        </svg>
        {t('chat.atalho.wiki')}
      </a>
      <BotaoVotar />
    </div>
  );
}

/**
 * Votar no TopIdle: aceso quando a conta já pode votar, apagado com a contagem até o próximo voto, e "aguardando"
 * depois do clique até o voto chegar (a recompensa vai para o correio). Some com a integração desligada.
 */
function BotaoVotar() {
  const { estado, aguardando, agora, votou } = useVotoTopIdle();
  if (!estado?.ligado || !estado.url) return null;
  const urna = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 13h16v7H4z" />
      <path d="M8 13 7 6h10l-1 7" />
      <path d="m9.5 9.5 2 2 3.5-4" />
    </svg>
  );

  if (!estado.podeVotar) {
    const { h, min } = tempoAteVotar((estado.proximoVotoEm ?? agora) - agora);
    const longo = h === 0 ? t('chat.atalho.tempoMin', { min }) : min === 0 ? t('chat.atalho.curtoH', { h }) : t('chat.atalho.tempoHMin', { h, min });
    const curto = h > 0 ? t('chat.atalho.curtoH', { h }) : t('chat.atalho.tempoMin', { min });
    const dica = t('chat.atalho.votarEspera', { tempo: longo });
    return (
      <span className="ct-at ct-at--votar is-espera" role="link" aria-disabled="true" title={dica} aria-label={dica}>
        {urna}
        {t('chat.atalho.votar')}
        <span className="ct-at__tempo" aria-hidden="true">{curto}</span>
      </span>
    );
  }

  return (
    <a
      className={aguardando ? 'ct-at ct-at--votar is-aguardando' : 'ct-at ct-at--votar'}
      href={estado.url}
      target="_blank"
      rel="noopener noreferrer"
      title={aguardando ? t('chat.atalho.votarAguardandoDica') : t('chat.atalho.votarDica')}
      onClick={votou}
    >
      {urna}
      {aguardando ? t('chat.atalho.votarAguardando') : t('chat.atalho.votar')}
    </a>
  );
}
